import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { Unit } from "../models/unit";
import { config } from "../../shared/config";
import { map } from "rxjs/operators";
import { UpdateUnitNameResponse } from "../models/update-unit-name-response";
import { BaseHttpService } from "../../core/services/base-http.service";
import { StorageService } from "../../core/storage/storage.service";
import { UnitTagSchema } from "../models/unit-tag-schema";
import { UnitTag, UnitTagResponse } from "../models/unitTag";
import { UpdateUnitStateRequest } from '../models/update-unit-state-request';

@Injectable({
  providedIn: "root",
})
export class UnitsService extends BaseHttpService {
  public selectedUnit: Observable<Unit>;
  private selectedUnitSubject: BehaviorSubject<Unit>;
  private units;

  constructor(
    protected _http: HttpClient,
    protected _storage: StorageService,
  ) {
    super(_http, _storage);
    this.selectedUnitSubject = new BehaviorSubject<Unit>(null);
    this.selectedUnit = this.selectedUnitSubject.asObservable();
  }

  setSelectedUnit(unit: Unit) {
    this.selectedUnitSubject.next(unit);
  }

  setSelectedUnitById(unitId: number) {
    const selected = this.units.filter((i) => i.unitId === unitId);
    this.selectedUnitSubject.next(selected);
  }

  getUnitById(unitId: number): Unit {
    return this.units?.find((i) => i.unitId === unitId);
  }

  public get selectedUnitValue(): Unit {
    return this.selectedUnitSubject.value;
  }

  getUnitsByGroup(unitGroup: number[]): Observable<Unit[]> {
    const fields = ["*"];
    const filters = [
      {
        name: "unitgroups",
        value: unitGroup.join("|"),
      },
    ];

    return this.get(
      config.generateApiUrl("Units", fields, filters, "2.0")
    ).pipe(
      map((request) => {
        this.units = request["data"]["items"];
        return request["data"]["items"] as Unit[];
      })
    );
  }

  getUnitsByIds(unitIds: number[]): Observable<Unit[]> {
    const fields = ["*"];

    return this.get(
      config.generateApiUrl("Units", fields, null, "2.0") +
        `&unitIds=${unitIds.join(",")}`
    ).pipe(
      map((request) => {
        this.units = request["data"]["items"];
        return request["data"]["items"] as Unit[];
      })
    );
  }

  updateUnitName(unitId: number, name: string): Observable<Unit> {
    const body = new UpdateUnitNameResponse();
    body.newUnitName = name;
    body.unitId = unitId;

    return this._http
      .put(config.generateApiUrl("Units", null, null, "2.0"), body)
      .pipe(
        map((request) => {
          return request["data"] as Unit;
        })
      );
  }

  updateUnitState(unitId: number, unitState: number): Observable<Unit> {
    const body: UpdateUnitStateRequest = {unitState};

    return this._http
        .put(
            config.generateApiUrl(`Units/${unitId}`, null, null, '2.0'),
            body
        )
        .pipe(
            map((response) => {
              return response['data'] as Unit;
            })
        );
  }

  getUnits(): Observable<Unit[]> {
    const fields = ["*"];

    return this.get(config.generateApiUrl("Units", fields, null, "2.0")).pipe(
      map((request) => {
        this.units = request["data"]["items"];
        return request["data"]["items"] as Unit[];
      })
    );
  }

  getUnitTagSchema(): Observable<UnitTagSchema[]> {
    const fields = ["*"];
    return this.get(config.generateApiUrl("UnitTag", fields, null, "2.0")).pipe(
      map((request) => {
        return request["data"]["items"];
      })
    );
  }
  getUnitTagsByUnitId(unitId: number): Observable<UnitTag[]> {
    const fields = ["*"];

    return this.get(
      config.generateApiUrl(`UnitTag/unit/${unitId}`, fields, null, "2.0")
    ).pipe(
      map((request) => {
        return request["data"]["items"] as UnitTag[];
      })
    );
  }
  createUnitTag(data: {}): Observable<UnitTagResponse> {
    const fields = ["*"];

    return this._http
      .post(config.generateApiUrl("UnitTag", fields, null, "2.0"), data)
      .pipe(
        map((request) => {
          return request["data"] as UnitTagResponse;
        })
      );
  }
  updateUnitTag(unitTagID: number, data: {}): Observable<UnitTagResponse> {
    const fields = ["*"];

    return this._http
      .put(
        config.generateApiUrl(`UnitTag/${unitTagID}`, fields, null, "2.0"),
        data
      )
      .pipe(
        map((request) => {
          return request as UnitTagResponse;
        })
      );
  }

  deleteUnitTag(unitTagID: number): Observable<UnitTag> {
    const fields = ["*"];
    return this._http
      .delete(
        config.generateApiUrl(`UnitTag/${unitTagID}`, fields, null, "2.0")
      )
      .pipe(
        map((request) => {
          return request as UnitTag;
        })
      );
  }
}
